import React, { useEffect, useState, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { api } from '../services/api';
import { PoS as Customer } from '../types';
import { Link } from 'react-router-dom';
import {
  Plus, Search, MapPin, Store, Edit2, Trash2, Coffee, Wine,
  UtensilsCrossed, Building2, Briefcase, Music, Soup, Truck,
  PartyPopper, Star, ChevronDown, X, Dumbbell, Flower2, PersonStanding,
} from 'lucide-react';
import {
  isHorecaType,
  STORE_TYPE_CATEGORIES,
} from '../components/visits/StoreTypeSelector';
import { ConfirmDialog } from '../components/ui/ConfirmDialog';

type SortOption = 'is_premium' | 'created_at' | 'name';

interface FilterState {
  search: string;
  city: string;
  district: string;
  store_type: string;
  is_premium: boolean | undefined;
  sort_by: SortOption;
  sort_dir: 'asc' | 'desc';
}

const SORT_LABELS: Record<SortOption, string> = {
  is_premium: 'Premium first',
  created_at: 'Newest',
  name: 'A – Z',
};

const LEGACY_STORE_TYPES = [
  { value: 'supermarket', label: 'Supermarket' },
  { value: 'kiosk', label: 'Kiosk' },
];

const Customers: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<string | null>(null);
  const [sortOpen, setSortOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const sortRef = useRef<HTMLDivElement>(null);

  const [filters, setFilters] = useState<FilterState>({
    search: '',
    city: '',
    district: '',
    store_type: '',
    is_premium: undefined,
    sort_by: 'is_premium',
    sort_dir: 'desc',
  });

  const searchTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const limit = 50;

  const buildQuery = useCallback(
    (f: FilterState, pg: number) => {
      const params = new URLSearchParams();
      params.set('page', String(pg));
      params.set('limit', String(limit));
      if (f.search) params.set('search', f.search);
      if (f.city) params.set('city', f.city);
      if (f.district) params.set('district', f.district);
      if (f.store_type) params.set('store_type', f.store_type);
      if (f.is_premium !== undefined) params.set('is_premium', String(f.is_premium));
      params.set('sort_by', f.sort_by);
      params.set('sort_dir', f.sort_dir);
      return `/pos?${params.toString()}`;
    },
    [limit]
  );

  const fetchCustomers = useCallback(
    async (f: FilterState, pg: number) => {
      try {
        setLoading(true);
        const response = await api.get(buildQuery(f, pg));
        const responseData = response.data;
        const list = Array.isArray(responseData) ? responseData : (responseData.data || []);
        setCustomers(list);
        setTotal(responseData.total || list.length);
      } catch (error) {
        console.error('Failed to fetch PoS:', error);
        setCustomers([]);
      } finally {
        setLoading(false);
      }
    },
    [buildQuery]
  );

  // Re-fetch when page changes (filters already applied)
  useEffect(() => {
    fetchCustomers(filters, page);
  }, [page]);

  // Debounce search; instant for non-search filter changes
  const handleFilterChange = (update: Partial<FilterState>, debounce = false) => {
    const next = { ...filters, ...update };
    setFilters(next);
    setPage(1);

    if (searchTimeout.current) clearTimeout(searchTimeout.current);

    if (debounce) {
      searchTimeout.current = setTimeout(() => fetchCustomers(next, 1), 300);
    } else {
      fetchCustomers(next, 1);
    }
  };

  // Close sort dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (sortRef.current && !sortRef.current.contains(e.target as Node)) {
        setSortOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleDelete = async () => {
    if (!customerToDelete) return;
    try {
      await api.delete(`/pos/${customerToDelete}`);
      toast.success('PoS deleted');
      fetchCustomers(filters, page);
    } catch (error) {
      console.error('Failed to delete PoS:', error);
      toast.error('Failed to delete PoS');
    }
  };

  const openDeleteDialog = (id: string) => {
    setCustomerToDelete(id);
    setDeleteDialogOpen(true);
  };

  const activeFilterCount = [
    filters.city,
    filters.district,
    filters.store_type,
    filters.is_premium !== undefined,
  ].filter(Boolean).length;

  const StoreIcon = ({ type }: { type: string }) => {
    const onPremiseIcons: Record<string, React.ReactNode> = {
      cafe: <Coffee size={20} />,
      bar: <Wine size={20} />,
      restaurant: <UtensilsCrossed size={20} />,
      hotel: <Building2 size={20} />,
      office: <Briefcase size={20} />,
      club: <Music size={20} />,
      bistro: <Soup size={20} />,
      kantine: <UtensilsCrossed size={20} />,
      catering: <Truck size={20} />,
      event_location: <PartyPopper size={20} />,
      pilates: <PersonStanding size={20} />,
      yoga: <Flower2 size={20} />,
      fitness: <Dumbbell size={20} />,
    };

    if (isHorecaType(type)) {
      return (
        <div className="p-2 bg-amber-100 text-amber-600 rounded-lg">
          {onPremiseIcons[type] || <Store size={20} />}
        </div>
      );
    }
    if (type === 'spaeti') {
      return <div className="p-2 bg-purple-100 text-purple-600 rounded-lg"><Store size={20} /></div>;
    }
    return <div className="p-2 bg-blue-100 text-blue-600 rounded-lg"><Store size={20} /></div>;
  };

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Points of Sale</h1>
          <p className="text-gray-500 text-sm">
            {total > 0 ? `${total} locations` : 'Manage your territory and store database'}
          </p>
        </div>
        <Link
          to="/pos/new"
          className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500"
        >
          <Plus size={20} className="mr-2" />
          Add PoS
        </Link>
      </div>

      {/* Search + Sort row */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search size={18} className="text-gray-400" />
          </div>
          <input
            type="text"
            value={filters.search}
            onChange={(e) => handleFilterChange({ search: e.target.value }, true)}
            className="block w-full pl-9 pr-3 py-2.5 border border-gray-200 rounded-lg bg-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 text-sm shadow-sm"
            placeholder="Search by name, address, city, district…"
          />
          {filters.search && (
            <button
              onClick={() => handleFilterChange({ search: '' })}
              className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
            >
              <X size={16} />
            </button>
          )}
        </div>

        {/* Filter toggle */}
        <button
          onClick={() => setFilterOpen((v) => !v)}
          className={`relative inline-flex items-center gap-1.5 px-3 py-2.5 border rounded-lg text-sm font-medium shadow-sm transition-colors ${
            activeFilterCount > 0
              ? 'border-brand-500 bg-brand-50 text-brand-700'
              : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
          }`}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="flex-shrink-0">
            <path d="M2 4h12M4 8h8M6 12h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
          Filters
          {activeFilterCount > 0 && (
            <span className="ml-1 inline-flex items-center justify-center w-4 h-4 rounded-full bg-brand-600 text-white text-xs font-bold">
              {activeFilterCount}
            </span>
          )}
        </button>

        {/* Sort dropdown */}
        <div className="relative" ref={sortRef}>
          <button
            onClick={() => setSortOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 px-3 py-2.5 border border-gray-200 rounded-lg text-sm font-medium bg-white text-gray-600 hover:bg-gray-50 shadow-sm"
          >
            {SORT_LABELS[filters.sort_by]}
            <ChevronDown size={14} className={`transition-transform ${sortOpen ? 'rotate-180' : ''}`} />
          </button>
          {sortOpen && (
            <div className="absolute right-0 mt-1 w-40 bg-white border border-gray-200 rounded-lg shadow-lg z-10 overflow-hidden">
              {(Object.keys(SORT_LABELS) as SortOption[]).map((key) => (
                <button
                  key={key}
                  onClick={() => {
                    handleFilterChange({ sort_by: key, sort_dir: key === 'name' ? 'asc' : 'desc' });
                    setSortOpen(false);
                  }}
                  className={`w-full text-left px-4 py-2.5 text-sm hover:bg-gray-50 ${
                    filters.sort_by === key ? 'font-semibold text-brand-600' : 'text-gray-700'
                  }`}
                >
                  {SORT_LABELS[key]}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Filter panel */}
      {filterOpen && (
        <div className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">City</label>
              <input
                type="text"
                value={filters.city}
                onChange={(e) => handleFilterChange({ city: e.target.value }, true)}
                placeholder="e.g. Berlin"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">District</label>
              <input
                type="text"
                value={filters.district}
                onChange={(e) => handleFilterChange({ district: e.target.value }, true)}
                placeholder="e.g. Kreuzberg"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Store type</label>
              <select
                value={filters.store_type}
                onChange={(e) => handleFilterChange({ store_type: e.target.value })}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-brand-500 bg-white"
              >
                <option value="">All types</option>
                {STORE_TYPE_CATEGORIES.map((category) => (
                  <optgroup key={category.category} label={category.category}>
                    {category.items.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
                <optgroup label="Legacy">
                  {LEGACY_STORE_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </optgroup>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">Premium</label>
              <div className="flex gap-2 mt-0.5">
                {[
                  { label: 'All', value: undefined },
                  { label: '★ Only', value: true },
                  { label: 'Standard', value: false },
                ].map((opt) => (
                  <button
                    key={String(opt.value)}
                    onClick={() => handleFilterChange({ is_premium: opt.value })}
                    className={`flex-1 py-1.5 text-xs rounded-lg border font-medium transition-colors ${
                      filters.is_premium === opt.value
                        ? 'border-brand-500 bg-brand-50 text-brand-700'
                        : 'border-gray-200 text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
          {activeFilterCount > 0 && (
            <button
              onClick={() => handleFilterChange({ city: '', district: '', store_type: '', is_premium: undefined })}
              className="mt-3 text-xs text-gray-500 hover:text-gray-700 underline"
            >
              Clear all filters
            </button>
          )}
        </div>
      )}

      {/* List */}
      {loading ? (
        <div className="grid grid-cols-1 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white p-4 rounded-xl shadow-sm border border-gray-100 h-24 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="bg-white shadow-sm border border-gray-100 rounded-xl overflow-hidden">
          <ul className="divide-y divide-gray-100">
            {customers.map((customer) => (
              <li key={customer.id} className="hover:bg-gray-50 transition-colors">
                <div className="px-6 py-4 flex items-center justify-between">
                  <Link to={`/pos/${customer.id}`} className="flex items-center min-w-0 gap-4 flex-1">
                    <StoreIcon type={customer.store_type} />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="text-sm font-semibold text-gray-900 truncate">{customer.name}</p>
                        {customer.is_premium && (
                          <Star
                            size={14}
                            className="text-amber-400 fill-amber-400 flex-shrink-0"
                            aria-label="Premium location"
                          />
                        )}
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium capitalize ${
                          isHorecaType(customer.store_type) ? 'bg-amber-50 text-amber-700' :
                          customer.store_type === 'spaeti' ? 'bg-purple-50 text-purple-700' :
                          'bg-blue-50 text-blue-700'
                        }`}>
                          {customer.store_type?.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="flex items-center mt-1 text-sm text-gray-500">
                        <MapPin size={14} className="mr-1 flex-shrink-0" />
                        <span className="truncate">
                          {customer.street
                            ? `${customer.street} ${customer.street_number}, ${customer.city}`
                            : `${customer.address}, ${customer.city}`}
                          {customer.district && (
                            <span className="text-gray-400"> · {customer.district}</span>
                          )}
                        </span>
                      </div>
                    </div>
                  </Link>

                  <div className="flex items-center gap-2 ml-4">
                    <Link
                      to={`/pos/${customer.id}/edit`}
                      className="p-2 text-gray-400 hover:text-brand-600 hover:bg-brand-50 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 size={18} />
                    </Link>
                    <button
                      onClick={() => openDeleteDialog(customer.id)}
                      className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </div>
              </li>
            ))}
            {customers.length === 0 && (
              <li className="px-6 py-12 text-center text-gray-500">
                <Store size={48} className="mx-auto text-gray-300 mb-4" />
                <p className="text-lg font-medium text-gray-900">No PoS found</p>
                <p className="mb-6 text-sm">
                  {activeFilterCount > 0 || filters.search
                    ? 'Try adjusting your search or filters.'
                    : 'Get started by adding your first location.'}
                </p>
                {!activeFilterCount && !filters.search && (
                  <Link
                    to="/pos/new"
                    className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700"
                  >
                    <Plus size={20} className="mr-2" />
                    Add PoS
                  </Link>
                )}
              </li>
            )}
          </ul>
        </div>
      )}

      {/* Pagination */}
      {total > limit && (
        <div className="flex justify-center gap-2">
          <button
            disabled={page === 1}
            onClick={() => setPage((p) => p - 1)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="px-3 py-1.5 text-sm text-gray-600">
            Page {page} of {Math.ceil(total / limit)}
          </span>
          <button
            disabled={page * limit >= total}
            onClick={() => setPage((p) => p + 1)}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title="Delete PoS?"
        message="Are you sure you want to delete this location? This action cannot be undone."
        confirmText="Delete PoS"
        variant="danger"
        onConfirm={handleDelete}
      />
    </div>
  );
};

export default Customers;
